import { useEffect, useMemo, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useTranslation } from 'react-i18next'
import { wordsApi, type WordWithTags } from '@/api/words'
import { tagsApi, type Tag } from '@/api/tagsApi'
import { dictionariesApi } from '@/api/dictionaries'
import type { Dictionary } from '@/types'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Skeleton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  TablePagination,
  Menu,
  Paper,
  Autocomplete,
  useMediaQuery,
  Tooltip,
  Link as MuiLink,
  Breadcrumbs,
} from '@mui/material'
import Link from '@mui/material/Link'
import { useTheme } from '@mui/material/styles'
import {
  Add,
  Edit,
  Delete,
  Search,
  MoreVert,
  Visibility as ViewIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  RecordVoiceOver,
  GroupAdd as GroupAddIcon,
} from '@mui/icons-material'
import Chip from '@mui/material/Chip'
import { useEnumsStore } from '@/stores/enumsStore'
import { useAuthStore } from '@/stores/authStore'
import { useBalanceStore } from '@/stores/balanceStore'
import { ROUTES } from '@/utils/constants'
import { parseSortValue } from '@/utils/sort'
import { getErrorMessage } from '@/utils/error'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { FiltersButton } from '@/components/common/FiltersButton'
import { WordAudioButton } from '@/components/words/WordAudioButton'
import { GenerateAudioDialog } from '@/components/tts/GenerateAudioDialog'
import { AssignWordsToStudentsDialog } from '@/components/bulk/AssignWordsToStudentsDialog'
import { formatMoney } from '@/components/billing/formatMoney'
import '@/styles/dashboard.css'

const buildWordSchema = (
  requireTeacherFields: boolean,
  messages: { wordRequired: string; transcriptionRequired: string; translationRequired: string },
) =>
  z.object({
    word: z.string().min(1, messages.wordRequired),
    transcription: requireTeacherFields
      ? z.string().min(1, messages.transcriptionRequired)
      : z.string().optional().or(z.literal('')),
    translation: requireTeacherFields
      ? z.string().min(1, messages.translationRequired)
      : z.string().optional().or(z.literal('')),
    part_of_speech: z.string().optional().or(z.literal('')),
    type: z.string().optional().or(z.literal('')),
    example_sentence: z.string().optional().or(z.literal('')),
    synonym: z.string().optional().or(z.literal('')),
    antonym: z.string().optional().or(z.literal('')),
    note: z.string().optional().or(z.literal('')),
    tag_ids: z.array(z.number()).optional(),
  })

const defaultWordSchema = buildWordSchema(true, {
  wordRequired: 'required',
  transcriptionRequired: 'required',
  translationRequired: 'required',
})

type WordFormData = z.infer<typeof defaultWordSchema>

const WORD_FORM_DEFAULTS: WordFormData = {
  word: '',
  transcription: '',
  translation: '',
  part_of_speech: '',
  type: '',
  example_sentence: '',
  synonym: '',
  antonym: '',
  note: '',
  tag_ids: [],
}

export function WordsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [words, setWords] = useState<WordWithTags[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setPageError] = useState<string | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()
  const [exact, setExact] = useState(() => searchParams.get('exact') === '1')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [wordToDelete, setWordToDelete] = useState<number | null>(null)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [wordToEdit, setWordToEdit] = useState<WordWithTags | null>(null)
  const [addDialogError, setAddDialogError] = useState<string | null>(null)
  const [editDialogError, setEditDialogError] = useState<string | null>(null)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [withoutTranslation, setWithoutTranslation] = useState(false)
  const [withoutTranscription, setWithoutTranscription] = useState(false)
  const [withoutAudio, setWithoutAudio] = useState(false)
  const [onlyImported, setOnlyImported] = useState(false)
  const [tagsFilter, setTagsFilter] = useState<number[]>([])
  const [partOfSpeech, setPartOfSpeech] = useState('')
  const [wordType, setWordType] = useState<'word' | 'phrase' | ''>('')
  const [dictionaryFilter, setDictionaryFilter] = useState<number | ''>('')
  const [notInAnyDictionary, setNotInAnyDictionary] = useState(false)
  const [dictionariesList, setDictionariesList] = useState<Dictionary[]>([])
  const [sortBy, setSortBy] = useState<'created_at' | 'text'>('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [perPage, setPerPage] = useState(15)
  const [tagsList, setTagsList] = useState<Tag[]>([])
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [menuWord, setMenuWord] = useState<WordWithTags | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [ttsDialogOpen, setTtsDialogOpen] = useState(false)
  const [ttsWordIds, setTtsWordIds] = useState<number[]>([])
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)

  /**
   * Счётчик для кнопки «Фильтры»: считаем только спрятанное под Collapse.
   * Поиск и «точное совпадение» видны в тулбаре всегда — с ними бейдж
   * загорался бы при пустой панели и врал бы учителю.
   */
  const appliedFiltersCount = useMemo(() => {
    let count = 0
    if (partOfSpeech !== '') count += 1
    if (wordType !== '') count += 1
    if (tagsFilter.length > 0) count += 1
    if (dictionaryFilter !== '') count += 1
    if (withoutTranslation) count += 1
    if (withoutTranscription) count += 1
    if (withoutAudio) count += 1
    if (onlyImported) count += 1
    if (notInAnyDictionary) count += 1

    return count
  }, [
    dictionaryFilter,
    notInAnyDictionary,
    onlyImported,
    partOfSpeech,
    tagsFilter,
    withoutAudio,
    withoutTranscription,
    withoutTranslation,
    wordType,
  ])

  const enums = useEnumsStore((state) => state.enums)
  const partOfSpeechOptions = enums?.PartOfSpeech ?? []
  const wordTypeOptions = enums?.WordType ?? []
  const currentUser = useAuthStore((s) => s.user)
  const requiresTeacherWordFields = useMemo(() => {
    const roles = currentUser?.roles ?? []
    return roles.some((r) => r.slug === 'teacher' || r.slug === 'admin')
  }, [currentUser?.roles])
  // Генерация озвучки доступна учителю/админу (ученикам — нет)
  const canGenerateAudio = requiresTeacherWordFields
  // Массовые действия над словами (озвучка, назначение ученикам) — те же роли.
  const canBulkSelect = requiresTeacherWordFields
  const balance = useBalanceStore((s) => s.balance)
  const balanceCurrency = useBalanceStore((s) => s.currency)
  const fetchBalance = useBalanceStore((s) => s.fetchBalance)

  useEffect(() => {
    if (canGenerateAudio) {
      void fetchBalance().catch((err) => {
        setPageError(getErrorMessage(err) || t('common.error'))
      })
    }
  }, [canGenerateAudio, fetchBalance, t])
  const wordValidationMessages = useMemo(
    () => ({
      wordRequired: t('words.validation.wordRequired'),
      transcriptionRequired: t('words.validation.transcriptionRequired'),
      translationRequired: t('words.validation.translationRequired'),
    }),
    [t],
  )
  const addWordSchema = useMemo(
    () => buildWordSchema(requiresTeacherWordFields, wordValidationMessages),
    [requiresTeacherWordFields, wordValidationMessages],
  )
  const editWordSchema = useMemo(
    () => buildWordSchema(requiresTeacherWordFields, wordValidationMessages),
    [requiresTeacherWordFields, wordValidationMessages],
  )

  const editTagsOptions = useMemo(() => {
    const byId = new Map(tagsList.map((t) => [t.id, t]))
    const fromWord = (wordToEdit?.tags ?? []).filter((t) => !byId.has(t.id)).map((t) => ({ id: t.id, name: t.name }))
    return [...tagsList, ...fromWord]
  }, [tagsList, wordToEdit?.tags])

  const addForm = useForm<WordFormData>({
    resolver: zodResolver(addWordSchema),
    defaultValues: WORD_FORM_DEFAULTS,
  })

  const editForm = useForm<WordFormData>({
    resolver: zodResolver(editWordSchema),
    defaultValues: WORD_FORM_DEFAULTS,
  })

  useEffect(() => {
    tagsApi.getList().then((r) => setTagsList(r.data)).catch(() => {})
  }, [])
  useEffect(() => {
    dictionariesApi.getDictionaries({ per_page: 100 }).then((r) => setDictionariesList(r.data)).catch(() => {})
  }, [])

  useEffect(() => {
    const timer = window.setTimeout(() => setAppliedSearch(searchInput.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  const loadWords = (overrides?: {
    page?: number
    per_page?: number
    search?: string
    exact?: boolean
    without_translation?: boolean
    without_transcription?: boolean
    without_audio?: boolean
    is_imported?: boolean
    tags?: number[]
    part_of_speech?: string
    type?: string
    order_by?: string
    order_direction?: 'asc' | 'desc'
    dictionary_id?: number
    not_in_any_dictionary?: boolean
  }) => {
    const params = {
      search: overrides?.search ?? (appliedSearch || undefined),
      ...(exact ? { exact: true } : {}),
      page: overrides?.page ?? page,
      per_page: overrides?.per_page ?? perPage,
      without_translation: overrides?.without_translation ?? withoutTranslation,
      without_transcription: overrides?.without_transcription ?? withoutTranscription,
      without_audio: overrides?.without_audio ?? withoutAudio,
      ...(overrides?.is_imported ?? onlyImported ? { is_imported: overrides?.is_imported ?? onlyImported } : {}),
      tags: ((overrides?.tags !== undefined ? overrides.tags : tagsFilter)?.length)
        ? (overrides?.tags ?? tagsFilter)
        : undefined,
      part_of_speech: overrides?.part_of_speech ?? (partOfSpeech || undefined),
      type: overrides?.type ?? (wordType || undefined),
      order_by: overrides?.order_by ?? sortBy,
      order_direction: overrides?.order_direction ?? sortOrder,
      dictionary_id: overrides?.dictionary_id ?? (dictionaryFilter || undefined),
      not_in_any_dictionary: overrides?.not_in_any_dictionary ?? notInAnyDictionary,
    }
    setIsLoading(true)
    setPageError(null)
    wordsApi
      .getWords(params)
      .then((res) => {
        setWords(res.data ?? [])
        setPage(res.current_page ?? 1)
        setTotal(res.total ?? 0)
        setPerPage(res.per_page ?? 15)
        setSelectedIds(new Set())
      })
      .catch((err) => {
        setPageError(err?.response?.data?.message || err?.message || t('words.loadError'))
      })
      .finally(() => setIsLoading(false))
  }

  useEffect(() => {
    setPage(1)
    loadWords({ page: 1 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedSearch, exact, withoutTranslation, withoutTranscription, withoutAudio, onlyImported, tagsFilter, partOfSpeech, wordType, sortBy, sortOrder, perPage, dictionaryFilter, notInAnyDictionary])

  const onExactChange = (checked: boolean) => {
    setExact(checked)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (checked) {
        next.set('exact', '1')
      } else {
        next.delete('exact')
      }
      return next
    })
  }

  const handlePageChange = (_: unknown, newPage: number) => {
    loadWords({ page: newPage + 1 })
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const v = parseInt(e.target.value, 10)
    setPerPage(v)
    setPage(1)
  }

  const handleDeleteClick = (id: number) => {
    setWordToDelete(id)
    setMenuAnchor(null)
    setMenuWord(null)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!wordToDelete) return
    try {
      await wordsApi.deleteWord(wordToDelete)
      setWords(words.filter((w) => w.id !== wordToDelete))
      setDeleteDialogOpen(false)
      setWordToDelete(null)
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string }
      setPageError(error?.response?.data?.message || error?.message || t('words.deleteError'))
      setDeleteDialogOpen(false)
      setWordToDelete(null)
    }
  }

  const toggleSelected = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const allOnPageSelected = words.length > 0 && words.every((w) => selectedIds.has(w.id))
  const someOnPageSelected = words.some((w) => selectedIds.has(w.id))

  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (allOnPageSelected) {
        words.forEach((w) => next.delete(w.id))
      } else {
        words.forEach((w) => next.add(w.id))
      }
      return next
    })
  }

  const openTtsDialog = (ids: number[]) => {
    setTtsWordIds(ids)
    setTtsDialogOpen(true)
  }

  const handleTtsFinished = () => {
    void fetchBalance()
    loadWords({ page })
  }

  const openMenu = (e: React.MouseEvent<HTMLElement>, w: WordWithTags) => {
    e.stopPropagation()
    setMenuAnchor(e.currentTarget)
    setMenuWord(w)
  }

  const closeMenu = () => {
    setMenuAnchor(null)
    setMenuWord(null)
  }

  const handleView = () => {
    if (menuWord) navigate(`/words/${menuWord.id}`)
    closeMenu()
  }

  const handleEditClick = () => {
    if (!menuWord) return
    setWordToEdit(menuWord)
    editForm.reset({
      ...WORD_FORM_DEFAULTS,
      word: menuWord.word,
      transcription: menuWord.transcription ?? '',
      translation: menuWord.translation ?? '',
      part_of_speech: menuWord.part_of_speech ?? '',
      type: menuWord.type ?? '',
      example_sentence: menuWord.example_sentence ?? '',
      synonym: menuWord.synonym ?? '',
      antonym: menuWord.antonym ?? '',
      note: menuWord.note ?? '',
      tag_ids: menuWord.tags?.map((x) => x.id) ?? [],
    })
    setEditDialogError(null)
    setEditDialogOpen(true)
    closeMenu()
  }

  const handleAddSubmit = async (data: WordFormData) => {
    try {
      setAddDialogError(null)
      const created = await wordsApi.createWord({
        word: (data.word || '').trim(),
        transcription: data.transcription?.trim() || undefined,
        translation: data.translation?.trim() || undefined,
        part_of_speech: data.part_of_speech?.trim() || undefined,
        type: data.type?.trim() || undefined,
        example_sentence: data.example_sentence?.trim() || undefined,
        synonym: data.synonym?.trim() || undefined,
        antonym: data.antonym?.trim() || undefined,
        note: data.note?.trim() || undefined,
        tag_ids: data.tag_ids?.length ? data.tag_ids : undefined,
      })
      setWords([created, ...words])
      addForm.reset(WORD_FORM_DEFAULTS)
      setAddDialogOpen(false)
    } catch (err: unknown) {
      const error = err as { response?: { data?: { errors?: { text?: string[] }; message?: string } }; message?: string }
      const apiErrors = error.response?.data?.errors
      if (apiErrors?.text?.[0]) {
        addForm.setError('word', { type: 'server', message: apiErrors.text[0] })
      } else {
        setAddDialogError(error?.response?.data?.message || error?.message || t('words.createError'))
      }
    }
  }

  const handleEditSubmit = async (data: WordFormData) => {
    if (!wordToEdit) return
    try {
      setEditDialogError(null)
      const updated = await wordsApi.updateWord(wordToEdit.id, {
        word: (data.word || '').trim(),
        transcription: data.transcription?.trim() ?? '',
        translation: data.translation?.trim() ?? '',
        part_of_speech: data.part_of_speech?.trim() ?? '',
        type: data.type?.trim() ?? '',
        example_sentence: data.example_sentence?.trim() ?? '',
        synonym: data.synonym?.trim() ?? '',
        antonym: data.antonym?.trim() ?? '',
        note: data.note?.trim() ?? '',
        tag_ids: data.tag_ids ?? [],
      })
      setWords(words.map((w) => (w.id === wordToEdit.id ? updated : w)))
      setEditDialogOpen(false)
      setWordToEdit(null)
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string }
      setEditDialogError(error?.response?.data?.message || error?.message || t('words.updateError'))
    }
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link component={RouterLink} to={ROUTES.DASHBOARD} underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <Typography color="text.primary">{t('words.title')}</Typography>
        </Breadcrumbs>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('words.title')}
          </Typography>
          <PageHelpPopover pageKey="words" />
          {canGenerateAudio && (
            <Button
              size="small"
              variant="outlined"
              component={RouterLink}
              to={ROUTES.TTS_TASKS}
              startIcon={<RecordVoiceOver />}
            >
              {t('tts.tasksTitle', 'Задачи озвучки')}
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPageError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder={t('words.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: isMobile ? 120 : 200, flex: isMobile ? 1 : undefined }}
          />
          <Tooltip title={t('common.searchExactTooltip')}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={exact}
                  onChange={(e) => onExactChange(e.target.checked)}
                />
              }
              label={t('common.searchExact')}
            />
          </Tooltip>
          <FormControl size="small" sx={{ minWidth: isMobile ? 140 : 200 }}>
            <InputLabel>{t('words.sortBy')}</InputLabel>
            <Select
              value={`${sortBy}_${sortOrder}`}
              label={t('words.sortBy')}
              onChange={(e) => {
                const [by, order] = parseSortValue<'created_at' | 'text'>(e.target.value as string)
                setSortBy(by)
                setSortOrder(order)
              }}
            >
              <MenuItem value="created_at_desc">{t('words.sort_created_at_desc')}</MenuItem>
              <MenuItem value="created_at_asc">{t('words.sort_created_at_asc')}</MenuItem>
              <MenuItem value="text_asc">{t('words.sort_text_asc')}</MenuItem>
              <MenuItem value="text_desc">{t('words.sort_text_desc')}</MenuItem>
            </Select>
          </FormControl>
          <FiltersButton
            open={filtersOpen}
            count={appliedFiltersCount}
            onToggle={() => setFiltersOpen((o) => !o)}
            label={t('words.filtersButton')}
          />
          <Box sx={{ ml: isMobile ? 0 : 'auto', display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={() => {
                addForm.reset(WORD_FORM_DEFAULTS)
                setAddDialogError(null)
                setAddDialogOpen(true)
              }}
              sx={{ height: 40 }}
            >
              {t('words.add')}
            </Button>
          </Box>
        </Box>

        <Collapse in={filtersOpen}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>{t('words.filterPartOfSpeech')}</InputLabel>
                <Select
                  value={partOfSpeech}
                  label={t('words.filterPartOfSpeech')}
                  onChange={(e) => setPartOfSpeech(e.target.value)}
                >
                  <MenuItem value="">{t('common.all')}</MenuItem>
                  {partOfSpeechOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>{t('words.filterType')}</InputLabel>
                <Select
                  value={wordType}
                  label={t('words.filterType')}
                  onChange={(e) => setWordType((e.target.value || '') as 'word' | 'phrase' | '')}
                >
                  <MenuItem value="">{t('common.all')}</MenuItem>
                  <MenuItem value="word">{t('cards.filter_type_word')}</MenuItem>
                  <MenuItem value="phrase">{t('cards.filter_type_phrase')}</MenuItem>
                </Select>
              </FormControl>
              <Autocomplete
                multiple
                size="small"
                options={tagsList}
                value={tagsList.filter((tag) => tagsFilter.includes(tag.id))}
                onChange={(_, newValue) => setTagsFilter(newValue.map((tag) => tag.id))}
                getOptionLabel={(tag) => tag.name}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderInput={(params) => (
                  <TextField {...params} label={t('words.filterTags')} placeholder={t('words.filterTagsPlaceholder')} />
                )}
                sx={{ minWidth: 220 }}
              />
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>{t('words.filterDictionary')}</InputLabel>
                <Select
                  value={dictionaryFilter === '' ? '' : String(dictionaryFilter)}
                  label={t('words.filterDictionary')}
                  onChange={(e) => setDictionaryFilter(e.target.value === '' ? '' : Number(e.target.value))}
                >
                  <MenuItem value="">{t('common.all')}</MenuItem>
                  {dictionariesList.map((d) => (
                    <MenuItem key={d.id} value={String(d.id)}>
                      {d.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0, alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={withoutTranslation}
                    onChange={(e) => setWithoutTranslation(e.target.checked)}
                  />
                }
                label={t('words.filterWithoutTranslation')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={withoutTranscription}
                    onChange={(e) => setWithoutTranscription(e.target.checked)}
                  />
                }
                label={t('words.filterWithoutTranscription')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={withoutAudio}
                    onChange={(e) => setWithoutAudio(e.target.checked)}
                  />
                }
                label={t('words.filterWithoutAudio')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={onlyImported}
                    onChange={(e) => setOnlyImported(e.target.checked)}
                  />
                }
                label={t('words.filterImported')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={notInAnyDictionary}
                    onChange={(e) => setNotInAnyDictionary(e.target.checked)}
                  />
                }
                label={t('words.filterNotInAnyDictionary')}
              />
            </Box>
          </Box>
        </Collapse>

        {canBulkSelect && selectedIds.size > 0 && (
          <Paper
            variant="outlined"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1,
              mb: 1,
            }}
          >
            <Typography variant="body2">
              {t('tts.selectedCount', 'Выбрано слов: {{count}}', { count: selectedIds.size })}
            </Typography>
            {canGenerateAudio && balance !== null && (
              <Chip
                size="small"
                label={`${t('tts.balance', 'Баланс')}: ${formatMoney(balance, balanceCurrency)}`}
              />
            )}
            <Box sx={{ flex: 1 }} />
            <Button size="small" onClick={() => setSelectedIds(new Set())}>
              {t('tts.clearSelection', 'Снять выделение')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<GroupAddIcon />}
              onClick={() => setAssignDialogOpen(true)}
            >
              {t('words.bulk.assignToStudents')}
            </Button>
            {canGenerateAudio && (
              <Button
                size="small"
                variant="contained"
                startIcon={<RecordVoiceOver />}
                onClick={() => openTtsDialog(Array.from(selectedIds))}
              >
                {t('tts.generate', 'Озвучить')}
              </Button>
            )}
          </Paper>
        )}

        <Card>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            {isLoading ? (
              <Box sx={{ p: 2 }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={52} sx={{ mb: 1, borderRadius: 1 }} />
                ))}
              </Box>
            ) : words.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
                <Typography variant="body1" color="text.secondary">
                  {t('words.noWords')}
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {canBulkSelect && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={allOnPageSelected}
                            indeterminate={someOnPageSelected && !allOnPageSelected}
                            onChange={toggleSelectAllOnPage}
                            inputProps={{ 'aria-label': t('tts.selectAll', 'Выбрать все на странице') }}
                          />
                        </TableCell>
                      )}
                      <TableCell>{t('words.word')}</TableCell>
                      <TableCell>{t('words.transcription')}</TableCell>
                      <TableCell>{t('words.translation')}</TableCell>
                      <TableCell>{t('words.createdAt')}</TableCell>
                      <TableCell align="right">{t('common.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {words.map((word) => {
                      const createdDate = word.created_at
                        ? (() => {
                            const d = new Date(word.created_at)
                            return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`
                          })()
                        : '—'
                      const isCreatedByMe = currentUser && word.creator?.id === currentUser.id
                      const creatorName = isCreatedByMe
                        ? t('common.you')
                        : (word.creator?.surname && word.creator?.first_name
                            ? `${word.creator.surname} ${word.creator.first_name}`
                            : word.creator?.email ?? '—')
                      const tooltipText = isCreatedByMe
                        ? t('words.createdByYouTooltip', { date: createdDate })
                        : t('words.createdByTooltip', { name: creatorName, date: createdDate })
                      return (
                        <TableRow key={word.id} hover selected={selectedIds.has(word.id)}>
                          {canBulkSelect && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                checked={selectedIds.has(word.id)}
                                onChange={() => toggleSelected(word.id)}
                              />
                            </TableCell>
                          )}
                          <TableCell>
                            <MuiLink
                              component={RouterLink}
                              to={`/words/${word.id}`}
                              underline="hover"
                              sx={{ fontWeight: 500 }}
                            >
                              {word.word}
                            </MuiLink>
                            <WordAudioButton audioUrl={word.audio_url} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {word.transcription || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {word.translation || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={tooltipText}>
                              <Typography variant="body2" color="text.secondary">
                                {createdDate}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={(e) => openMenu(e, word)} aria-label={t('common.actions')}>
                              <MoreVert fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {!isLoading && words.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              mt: 2,
              ...(isMobile && { flexDirection: 'column', alignItems: 'stretch' }),
            }}
          >
            <TablePagination
              component="div"
              count={total}
              page={Math.max(0, page - 1)}
              onPageChange={handlePageChange}
              rowsPerPage={perPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 15, 25, 50]}
              labelRowsPerPage={isMobile ? '' : t('words.rows_per_page')}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} ${t('words.of')} ${count !== -1 ? count : `${t('words.more_than')} ${to}`}`
              }
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: 'auto',
                ...(isMobile && { borderTop: 1, borderColor: 'divider', pt: 1 }),
              }}
            />
          </Box>
        )}

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={handleView}>
            <ViewIcon sx={{ mr: 1 }} fontSize="small" />
            {t('common.view')}
          </MenuItem>
          <MenuItem onClick={handleEditClick}>
            <Edit sx={{ mr: 1 }} fontSize="small" />
            {t('common.edit')}
          </MenuItem>
          {canGenerateAudio && (
            <MenuItem
              onClick={() => {
                if (menuWord) openTtsDialog([menuWord.id])
                closeMenu()
              }}
            >
              <RecordVoiceOver sx={{ mr: 1 }} fontSize="small" />
              {t('tts.generate', 'Озвучить')}
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              if (menuWord) handleDeleteClick(menuWord.id)
            }}
            sx={{ color: 'error.main' }}
          >
            <Delete sx={{ mr: 1 }} fontSize="small" />
            {t('common.delete')}
          </MenuItem>
        </Menu>

        {/* Add Word Dialog */}
        <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
          <Box component="form" onSubmit={addForm.handleSubmit(handleAddSubmit)}>
            <DialogTitle>{t('words.addTitle')}</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                {addDialogError && (
                  <Alert severity="error" onClose={() => setAddDialogError(null)}>
                    {addDialogError}
                  </Alert>
                )}
                <Controller
                  name="word"
                  control={addForm.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('words.word')}
                      fullWidth
                      required
                      size="small"
                      error={!!addForm.formState.errors.word}
                      helperText={addForm.formState.errors.word?.message}
                      autoFocus
                    />
                  )}
                />
                <Controller
                  name="transcription"
                  control={addForm.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('words.transcription')}
                      fullWidth
                      size="small"
                      required={requiresTeacherWordFields}
                      error={!!addForm.formState.errors.transcription}
                      helperText={addForm.formState.errors.transcription?.message}
                    />
                  )}
                />
                <Controller
                  name="translation"
                  control={addForm.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('words.translation')}
                      fullWidth
                      size="small"
                      required={requiresTeacherWordFields}
                      error={!!addForm.formState.errors.translation}
                      helperText={addForm.formState.errors.translation?.message}
                    />
                  )}
                />
                <Controller
                  name="part_of_speech"
                  control={addForm.control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('cards.filter.part_of_speech')}</InputLabel>
                      <Select {...field} label={t('cards.filter.part_of_speech')}>
                        <MenuItem value="">{t('common.none')}</MenuItem>
                        {partOfSpeechOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                <Controller
                  name="type"
                  control={addForm.control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('cards.filter.type')}</InputLabel>
                      <Select {...field} label={t('cards.filter.type')}>
                        <MenuItem value="">{t('common.none')}</MenuItem>
                        {wordTypeOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                <Controller
                  name="example_sentence"
                  control={addForm.control}
                  render={({ field }) => (
                    <TextField {...field} label={t('cards.word_attrs.example_sentence')} fullWidth size="small" multiline />
                  )}
                />
                <Controller
                  name="synonym"
                  control={addForm.control}
                  render={({ field }) => (
                    <TextField {...field} label={t('cards.word_attrs.synonym')} fullWidth size="small" />
                  )}
                />
                <Controller
                  name="antonym"
                  control={addForm.control}
                  render={({ field }) => (
                    <TextField {...field} label={t('cards.word_attrs.antonym')} fullWidth size="small" />
                  )}
                />
                <Controller
                  name="note"
                  control={addForm.control}
                  render={({ field }) => (
                    <TextField {...field} label={t('cards.word_attrs.note')} fullWidth size="small" multiline />
                  )}
                />
                <Controller
                  name="tag_ids"
                  control={addForm.control}
                  render={({ field }) => (
                    <Autocomplete
                      multiple
                      size="small"
                      options={tagsList}
                      value={tagsList.filter((t) => (field.value ?? []).includes(t.id))}
                      onChange={(_, newValue) => field.onChange(newValue.map((x) => x.id))}
                      getOptionLabel={(tag) => tag.name}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      renderInput={(params) => (
                        <TextField {...params} label={t('words.filterTags')} placeholder={t('words.filterTagsPlaceholder')} />
                      )}
                    />
                  )}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setAddDialogOpen(false)} color="inherit">
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="contained" disabled={addForm.formState.isSubmitting}>
                {addForm.formState.isSubmitting ? t('common.loading') : t('words.add')}
              </Button>
            </DialogActions>
          </Box>
        </Dialog>

        {/* Edit Word Dialog */}
        <Dialog open={editDialogOpen} onClose={() => { setEditDialogOpen(false); setWordToEdit(null) }} maxWidth="sm" fullWidth>
          <Box component="form" onSubmit={editForm.handleSubmit(handleEditSubmit)}>
            <DialogTitle>{t('words.editTitle')}</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                {editDialogError && (
                  <Alert severity="error" onClose={() => setEditDialogError(null)}>
                    {editDialogError}
                  </Alert>
                )}
                <Controller
                  name="word"
                  control={editForm.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('words.word')}
                      fullWidth
                      required
                      size="small"
                      error={!!editForm.formState.errors.word}
                      helperText={editForm.formState.errors.word?.message}
                      autoFocus
                    />
                  )}
                />
                <Controller
                  name="transcription"
                  control={editForm.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('words.transcription')}
                      fullWidth
                      size="small"
                      required={requiresTeacherWordFields}
                      error={!!editForm.formState.errors.transcription}
                      helperText={editForm.formState.errors.transcription?.message}
                    />
                  )}
                />
                <Controller
                  name="translation"
                  control={editForm.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('words.translation')}
                      fullWidth
                      size="small"
                      required={requiresTeacherWordFields}
                      error={!!editForm.formState.errors.translation}
                      helperText={editForm.formState.errors.translation?.message}
                    />
                  )}
                />
                <Controller
                  name="part_of_speech"
                  control={editForm.control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('cards.filter.part_of_speech')}</InputLabel>
                      <Select {...field} label={t('cards.filter.part_of_speech')}>
                        <MenuItem value="">{t('common.none')}</MenuItem>
                        {partOfSpeechOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                <Controller
                  name="type"
                  control={editForm.control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('cards.filter.type')}</InputLabel>
                      <Select {...field} label={t('cards.filter.type')}>
                        <MenuItem value="">{t('common.none')}</MenuItem>
                        {wordTypeOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                <Controller
                  name="example_sentence"
                  control={editForm.control}
                  render={({ field }) => (
                    <TextField {...field} label={t('cards.word_attrs.example_sentence')} fullWidth size="small" multiline />
                  )}
                />
                <Controller
                  name="synonym"
                  control={editForm.control}
                  render={({ field }) => (
                    <TextField {...field} label={t('cards.word_attrs.synonym')} fullWidth size="small" />
                  )}
                />
                <Controller
                  name="antonym"
                  control={editForm.control}
                  render={({ field }) => (
                    <TextField {...field} label={t('cards.word_attrs.antonym')} fullWidth size="small" />
                  )}
                />
                <Controller
                  name="note"
                  control={editForm.control}
                  render={({ field }) => (
                    <TextField {...field} label={t('cards.word_attrs.note')} fullWidth size="small" multiline />
                  )}
                />
                <Controller
                  name="tag_ids"
                  control={editForm.control}
                  render={({ field }) => (
                    <Autocomplete
                      multiple
                      size="small"
                      options={editTagsOptions}
                      value={editTagsOptions.filter((t) => (field.value ?? []).includes(t.id))}
                      onChange={(_, newValue) => field.onChange(newValue.map((x) => x.id))}
                      getOptionLabel={(tag) => tag.name}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      renderInput={(params) => (
                        <TextField {...params} label={t('words.filterTags')} placeholder={t('words.filterTagsPlaceholder')} />
                      )}
                    />
                  )}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => { setEditDialogOpen(false); setWordToEdit(null) }} color="inherit">
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="contained" disabled={editForm.formState.isSubmitting}>
                {editForm.formState.isSubmitting ? t('common.loading') : t('common.save')}
              </Button>
            </DialogActions>
          </Box>
        </Dialog>

        <Dialog open={deleteDialogOpen} onClose={() => { setDeleteDialogOpen(false); setWordToDelete(null) }}>
          <DialogTitle>{t('words.deleteTitle')}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t('words.deleteConfirm')}</DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => { setDeleteDialogOpen(false); setWordToDelete(null) }} color="inherit">
              {t('common.cancel')}
            </Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              {t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Диалог TTS-озвучивания выбранных слов */}
        <GenerateAudioDialog
          open={ttsDialogOpen}
          wordIds={ttsWordIds}
          onClose={() => setTtsDialogOpen(false)}
          onFinished={handleTtsFinished}
        />

        {/* Массовое назначение выбранных слов ученикам */}
        <AssignWordsToStudentsDialog
          open={assignDialogOpen}
          wordIds={Array.from(selectedIds)}
          onClose={() => setAssignDialogOpen(false)}
          onAssigned={() => setSelectedIds(new Set())}
        />
      </Box>
    </DashboardLayout>
  )
}
